import React, { useState } from 'react';
import { Button, Menu, MenuItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import ApprovalOutlined from '@mui/icons-material/ApprovalOutlined';
import KeyOutlined from '@mui/icons-material/KeyOutlined';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { addStep } from '../../../state';
import { useOfferedStepTypes } from '../../../hooks/useOfferedStepTypes';
import { WorkflowStepType, StepConfiguration, STEP_TYPE_LABELS, createStepId } from '../../../../../services/WorkflowService';

const STEP_TYPE_OPTIONS: { type: WorkflowStepType; label: string; description: string; icon: React.ReactNode; defaultConfig: StepConfiguration }[] = [
  {
    type: WorkflowStepType.AutoAssignmentStep,
    label: 'Auto Assignment',
    description: 'Automatically assign the ticket based on rules',
    icon: <GroupsOutlined fontSize="small" />,
    defaultConfig: {
      assignmentRule: {
        type: 'Specific',
        targetUser: '',
        targetUserName: '',
        targetTeam: '',
        userPool: [],
        requiredSkills: [],
        maxWorkload: 10,
      },
    },
  },
  {
    type: WorkflowStepType.ApprovalStep,
    label: 'Approval',
    description: 'Require approval before proceeding',
    icon: <ApprovalOutlined fontSize="small" />,
    defaultConfig: {
      approverEmail: '',
      approvalMessage: '',
      requireComment: false,
      timeoutDuration: 0,
      timeoutUnit: 'Hours',
    },
  },
  {
    type: WorkflowStepType.HstProvisioningStep,
    label: STEP_TYPE_LABELS[WorkflowStepType.HstProvisioningStep],
    description: 'Create or update the HST user after approval',
    icon: <KeyOutlined fontSize="small" />,
    defaultConfig: {},
  },
];

export const AddStepButton: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentWorkflow = useAppSelector((state) => state.workflow.currentWorkflow);
  const offeredTypes = useOfferedStepTypes();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleAddStep = (option: typeof STEP_TYPE_OPTIONS[number]) => {
    if (!currentWorkflow) return;

    const nextOrder = currentWorkflow.steps.length + 1;
    dispatch(
      addStep({
        id: createStepId(),
        stepType: option.type,
        name: `${option.label} Step ${nextOrder}`,
        description: option.description,
        order: nextOrder,
        isRequired: false,
        timeout: 0,
        configuration: option.defaultConfig,
      })
    );
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ textTransform: 'none' }}
      >
        Add Step
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {STEP_TYPE_OPTIONS.filter((option) => offeredTypes.includes(option.type)).map((option) => (
          <MenuItem key={option.type} onClick={() => handleAddStep(option)} sx={{ py: 1.5 }}>
            <ListItemIcon sx={{ color: '#3b82f6' }}>{option.icon}</ListItemIcon>
            <ListItemText
              primary={option.label}
              secondary={
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  {option.description}
                </Typography>
              }
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default AddStepButton;
